/**
 * Submit Tab Component
 *
 * Matches original HTML structure: .submission-container, .spotify-search-section, etc.
 */

import { useState, useEffect } from 'preact/hooks';
import { selectedGroup, canSubmit } from '@/stores/groups';
import { hasSubmitted, userSubmission, submitSong, isSubmitting, deleteSong } from '@/stores/songs';
import {
  hasAnyMusicProvider,
  connectedProviders,
  activeProvider,
  getProviderAuthUrl,
} from '@/stores/auth';
import { toast } from '@/stores/ui';
import { musicApi } from '@/api/client';
import type { SpotifyTrack, AppleMusicTrack, UnifiedTrack, MusicProvider } from '@/types';

type SearchResult = SpotifyTrack | AppleMusicTrack | UnifiedTrack;

export function SubmitTab() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState<SearchResult | null>(null);
  const [comment, setComment] = useState('');
  const [searchProvider, setSearchProvider] = useState<MusicProvider | null>(null);

  // Safe access to signals
  const providers = connectedProviders.value || [];
  const active = activeProvider.value;
  const group = selectedGroup.value;
  const hasProvider = hasAnyMusicProvider.value;

  useEffect(() => {
    if (!searchProvider && providers.length > 0) {
      setSearchProvider(active || providers[0]);
    }
  }, [providers, active, searchProvider]);

  // No group selected
  if (!group) {
    return (
      <div class="submission-container">
        <div class="welcome-section">
          <h2>Select a pod first</h2>
          <p>Choose a pod from the bar above to submit a song</p>
        </div>
      </div>
    );
  }

  // No music provider connected
  if (!hasProvider) {
    return (
      <div class="submission-container">
        <div class="submission-header">
          <div class="submission-status not-submitted">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>Connect Spotify to submit songs</span>
          </div>
        </div>
        <div class="submit-form-container">
          <div class="spotify-search-section">
            <h3 class="submit-section-title">Connect Your Music</h3>
            <p>Connect your Spotify account to search and submit songs.</p>
            <button
              class="btn btn-spotify"
              onClick={() => window.location.href = getProviderAuthUrl('spotify')}
            >
              <svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
              Connect with Spotify
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Submission period closed
  if (!canSubmit.value) {
    return (
      <div class="submission-container">
        <div class="submission-header">
          <div class="submission-status">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span>Submissions are closed for {group.currentMonth}</span>
          </div>
        </div>
      </div>
    );
  }

  // Already submitted
  if (hasSubmitted.value && userSubmission.value) {
    return (
      <div class="submission-container">
        <div class="submission-header">
          <div class="submission-status submitted">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <span>You've submitted for {group.currentMonth} {group.currentYear}</span>
          </div>
        </div>

        <div class="submit-form-container">
          <div id="submit-selected-track" class="selected-track-section">
            <h3 class="submit-section-title">Your Submission</h3>
            <div class="selected-track-info">
              <div class="selected-track-image">
                <img
                  src={userSubmission.value.albumArt || '/api/placeholder/100/100'}
                  alt="Album cover"
                />
              </div>
              <div class="selected-track-details">
                <h4>{userSubmission.value.title}</h4>
                <p>{userSubmission.value.artist}</p>
                {userSubmission.value.comments && (
                  <p class="selected-track-comment">"{userSubmission.value.comments}"</p>
                )}
                <button
                  class="btn btn-light btn-sm"
                  onClick={async () => {
                    if (confirm('Are you sure you want to delete your submission?')) {
                      const success = await deleteSong(userSubmission.value!.id);
                      if (success) {
                        toast.success('Submission deleted');
                      } else {
                        toast.error('Failed to delete submission');
                      }
                    }
                  }}
                >
                  Delete Submission
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Search handler
  const handleSearch = async () => {
    if (!query.trim() || !searchProvider) return;
    setIsSearching(true);
    try {
      const response = await musicApi.search(query, searchProvider);
      if (response.data?.tracks) {
        setResults(response.data.tracks);
      }
    } catch (e) {
      toast.error('Search failed');
      console.error('Search error:', e);
    } finally {
      setIsSearching(false);
    }
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!selectedTrack || !searchProvider) return;
    const trackId = getTrackId(selectedTrack);
    if (!trackId) {
      toast.error('Invalid track selection');
      return;
    }

    const song = await submitSong({
      trackId,
      provider: searchProvider,
      groupId: group!.id,
      comments: comment || undefined,
    });

    if (song) {
      toast.success('Song submitted!');
      setSelectedTrack(null);
      setComment('');
      setQuery('');
      setResults([]);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
  };

  return (
    <div class="submission-container">
      {/* Submission status header */}
      <div class="submission-header">
        <div class="submission-status not-submitted">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="color: #636e72">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>You haven't submitted a song for {group.currentMonth} {group.currentYear} yet</span>
        </div>
        <div class="countdown-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          <span id="submit-countdown-timer">Submission open</span>
        </div>
      </div>

      {/* Submission form */}
      <div class="submit-form-container">
        {/* Spotify search section */}
        <div class="spotify-search-section">
          <h3 class="submit-section-title">Search for a song</h3>

          <div class="spotify-search-container">
            <div class="spotify-search-input-wrapper">
              <input
                type="text"
                id="submit-spotify-search"
                placeholder="Type to search Spotify..."
                class="spotify-search-input"
                autocomplete="off"
                value={query}
                onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
              {query && (
                <button class="spotify-search-clear" id="submit-search-clear" onClick={clearSearch}>
                  ×
                </button>
              )}
            </div>

            {/* Search results */}
            <div id="submit-search-results" class="spotify-search-results">
              {isSearching && <div class="search-loading">Searching...</div>}
              {!isSearching && results.map((track) => (
                <div
                  key={getTrackId(track)}
                  class="spotify-result-item"
                  onClick={() => {
                    setSelectedTrack(track);
                    setResults([]);
                  }}
                >
                  <img
                    src={getTrackArtwork(track, 'small')}
                    alt=""
                    class="result-image"
                  />
                  <div class="result-details">
                    <div class="result-title">{getTrackName(track)}</div>
                    <div class="result-artist">{getTrackArtist(track)}</div>
                  </div>
                  <div class="result-duration">{formatDuration(getTrackDuration(track))}</div>
                </div>
              ))}
            </div>

            <div class="spotify-search-attribution">
              <a href="https://spotify.com" target="_blank" rel="noopener noreferrer">
                <img src="/images/Full_Logo_Green_RGB.svg" alt="Spotify" />
              </a>
            </div>
          </div>
        </div>

        {/* Selected track section */}
        {selectedTrack && (
          <div id="submit-selected-track" class="selected-track-section">
            <h3 class="submit-section-title">Selected Track</h3>

            <div class="selected-track-info">
              <div class="selected-track-image">
                <img
                  src={getTrackArtwork(selectedTrack)}
                  alt="Album cover"
                />
              </div>
              <div class="selected-track-details">
                <h4>{getTrackName(selectedTrack)}</h4>
                <p>{getTrackArtist(selectedTrack)}</p>
                <p class="selected-track-album">{getTrackAlbum(selectedTrack)}</p>
                <button
                  id="change-track-btn"
                  class="btn btn-light btn-sm"
                  onClick={() => setSelectedTrack(null)}
                >
                  Change Track
                </button>
              </div>
            </div>

            {/* Comment textarea */}
            <div class="submission-comment-section">
              <label for="submission-comment">Add a comment (optional)</label>
              <textarea
                id="submission-comment"
                placeholder="Why did you pick this song?"
                value={comment}
                onInput={(e) => setComment((e.target as HTMLTextAreaElement).value)}
                maxLength={280}
              />
              <span class="char-count">{comment.length}/280</span>
            </div>
          </div>
        )}

        {/* Submission actions */}
        <div class="submission-actions submit-page-actions">
          <p class="submission-message">
            Share your favorite song this month and join the voting!
          </p>
          <button
            id="final-submit-btn"
            class="btn btn-primary"
            disabled={!selectedTrack || isSubmitting.value}
            onClick={handleSubmit}
          >
            {isSubmitting.value ? 'Submitting...' : 'Submit Song'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper functions
function getTrackId(track: SearchResult): string | null {
  if ('id' in track) return track.id;
  return null;
}

function getTrackName(track: SearchResult): string {
  if ('name' in track) return track.name;
  if ('attributes' in track) return track.attributes.name;
  return 'Unknown';
}

function getTrackArtist(track: SearchResult): string {
  if ('artists' in track && Array.isArray(track.artists)) {
    return track.artists.map((a: { name: string }) => a.name).join(', ');
  }
  if ('attributes' in track) return track.attributes.artistName;
  return 'Unknown Artist';
}

function getTrackAlbum(track: SearchResult): string {
  if ('album' in track && typeof track.album === 'object' && 'name' in track.album) {
    return track.album.name;
  }
  if ('attributes' in track) return track.attributes.albumName;
  return '';
}

function getTrackArtwork(track: SearchResult, size: 'small' | 'large' = 'large'): string {
  if ('album' in track && typeof track.album === 'object' && 'images' in track.album) {
    const images = track.album.images;
    const index = size === 'small' ? Math.min(2, images.length - 1) : 0;
    return images[index]?.url || '/api/placeholder/100/100';
  }
  if ('attributes' in track && track.attributes.artwork) {
    const width = size === 'small' ? 100 : 300;
    return track.attributes.artwork.url
      .replace('{w}', String(width))
      .replace('{h}', String(width));
  }
  return '/api/placeholder/100/100';
}

function getTrackDuration(track: SearchResult): number {
  if ('duration_ms' in track) return track.duration_ms;
  if ('durationMs' in track) return track.durationMs;
  if ('attributes' in track) return track.attributes.durationInMillis;
  return 0;
}

function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
